import { call, put, select } from "redux-saga/effects";
import { albumBuilderConstants as C } from "../actions/constants/albumBuilderConstants";
import albumBuilderService from "../services/albumBuilderService";
import { commonActions } from "../actions/commonAction";
import { PayloadAction } from "@reduxjs/toolkit";

export function* fetchTemplate(action: any): Generator<any, void, any> {
  try {
    const response = yield call(albumBuilderService.fetchTemplate, action.payload);
    if (response?.success) {
      yield put({ type: C.fetchTemplateSuccess, payload: response.resultObject });
    } else {
      const msg = response?.message || "Failed to fetch album template";
      yield put({ type: C.fetchTemplateFailure, payload: msg });
      yield put(commonActions.errorResponse(msg));
    }
  } catch (error: any) {
    const msg = error?.message || "An error occurred";
    yield put({ type: C.fetchTemplateFailure, payload: msg });
    yield put(commonActions.errorResponse(msg));
  }
}

export function* createTemplate(action: any): Generator<any, void, any> {
    try {
        const response = yield call(albumBuilderService.createTemplate, action.payload);
        if (response?.success) {
            const template = response.resultObject;
            yield put({ type: C.createTemplateSuccess, payload: template });
        } else {
            const msg = response?.message || "Failed to create album template";
            yield put({ type: C.createTemplateFailure, payload: msg });
            yield put(commonActions.errorResponse(msg));
        }
    } catch (error: any) {
        const msg = error?.message || "An error occurred";
        yield put({ type: C.createTemplateFailure, payload: msg });
        yield put(commonActions.errorResponse(msg));
    }

}

export function* updateTemplate(action: any): Generator<any, void, any> {
    try {
        const response = yield call(albumBuilderService.updateTemplate, action.payload);
        if (response?.success) {
            yield put({ type: C.updateTemplateSuccess, payload: response.resultObject });
        } else {
            const msg = response?.message || "Failed to create album template";
            yield put({ type: C.updateTemplateFailure, payload: msg });
            yield put(commonActions.errorResponse(msg));
        }
    } catch (error: any) {
        const msg = error?.message || "An error occurred";
        yield put({ type: C.updateTemplateFailure, payload: msg });
        yield put(commonActions.errorResponse(msg));
    }
}


export function* listTemplates(): Generator<any, void, any> {
  try {
    const response = yield call(albumBuilderService.listTemplates);
    if (response?.success) {
      yield put({ type: C.listTemplatesSuccess, payload: response.resultObject });
    } else {
      const msg = response?.message || "Failed to list templates";
      yield put({ type: C.listTemplatesFailure, payload: msg });
    }
  } catch (error: any) {
    const msg = error?.message || "An error occurred";
    yield put({ type: C.listTemplatesFailure, payload: msg });
  }
}

export function* fetchPage(action: any): Generator<any, void, any> {
  try {
    const { pageId } = action.payload;
    const existingPages: Record<string, any> = yield select((s: any) => s.albumBuilder.pages);
    if (existingPages[pageId]) return;

    yield put({ type: C.setPageLoading, payload: pageId });
    const response = yield call(albumBuilderService.fetchPage, action.payload);
    if (response?.success) {
      yield put({ type: C.fetchPageSuccess, payload: response.resultObject });
    } else {
      const msg = response?.message || "Failed to fetch album page";
      yield put({ type: C.fetchPageFailure, payload: { pageId, message: msg } });
    }
  } catch (error: any) {
    const msg = error?.message || "An error occurred";
    yield put({ type: C.fetchPageFailure, payload: { pageId: action.payload.pageId, message: msg } });
  }
}

export function* fetchPages(action: any): Generator<any, void, any> {
    try {
        const { pageIds, albumId } = action.payload;

        if (!pageIds?.length) return;

        const existingPages: Record<string, any> = yield select(
            (s: any) => s.albumBuilder.pages,
        );

        const missingPageIds = pageIds.filter(
            (pageId: string) => !existingPages[pageId],
        );

        if (!missingPageIds.length) return;

        const response = yield call(albumBuilderService.fetchPagesApi, {
            pageIds: missingPageIds,
            albumId,
        });

        if (response?.success) {
            yield put({
                type: C.fetchAlbumPagesSuccess,
                payload: response.resultObject,
            });
        } else {
            const msg = response?.message || "Failed to fetch album pages";

            yield put({
                type: C.fetchAlbumPagesFailure,
                payload: msg,
            });
        }
    } catch (error: any) {
        const msg = error?.message || "An error occurred";

        yield put({
            type: C.fetchAlbumPagesFailure,
            payload: msg,
        });
    }
}

export function* preparePreview(
    action: any,
): Generator<any, void, any> {
    try {
        const { pageIds, albumId, templateId } = action.payload;

        if ((!albumId && ! templateId)) {
            throw new Error("Album ID is required");
        }

        if (!pageIds?.length) {
            yield put({
                type: C.preparePreviewPagesReady,
            });

            return;
        }

        const existingPages: Record<string, any> =
            yield select(
                (s: any) => s.albumBuilder.pages,
            );

        const missingPageIds = pageIds.filter(
            (pageId: string) => !existingPages[pageId],
        );

        let response;
        if (missingPageIds.length > 0) {
            if(templateId){
              response = yield call(albumBuilderService.fetchPage,{ pageIds: missingPageIds, templateId })
            } else { 
            response = yield call(
                albumBuilderService.fetchPagesApi,
                {
                    pageIds: missingPageIds,
                    albumId,
                },
            );
          }
            if (!response?.success) {
                throw new Error(
                    response?.message ??
                    "Failed to load album pages",
                );
            }

            yield put({
                type: C.fetchAlbumPagesSuccess,
                payload: response.resultObject ?? [],
            });
        }

        yield put({
            type: C.preparePreviewPagesReady,
        });
    } catch (error: any) {
        yield put({
            type: C.preparePreviewFailure,
            payload:
                error?.message ??
                "Failed to prepare album preview",
        });
    }
}
export function* createPage(action: any): Generator<any, void, any> {
  try {
    const response = yield call(albumBuilderService.createPage, action.payload);
    if (response?.success) {
      const page   = response.resultObject;
      const pageId = page._id ?? page.id;
      yield put({ type: C.createPageSuccess, payload: page });
      yield put({ type: C.setActivePage, payload: pageId });
    } else {
      const msg = response?.message || "Failed to create album page";
      yield put({ type: C.createPageFailure, payload: msg });
      yield put(commonActions.errorResponse(msg));
    }
  } catch (error: any) {
    const msg = error?.message || "An error occurred";
    yield put({ type: C.createPageFailure, payload: msg });
    yield put(commonActions.errorResponse(msg));
  }
}

export function* deletePage(action: any): Generator<any, void, any> {
  try {
    const response = yield call(albumBuilderService.deletePage, action.payload);
    if (response?.success) {
      yield put({ type: C.deletePageSuccess, payload: action.payload.pageId });
    } else {
      const msg = response?.message || "Failed to delete album page";
      yield put({ type: C.deletePageFailure, payload: msg });
      yield put(commonActions.errorResponse(msg));
    }
  } catch (error: any) {
    const msg = error?.message || "An error occurred";
    yield put({ type: C.deletePageFailure, payload: msg });
    yield put(commonActions.errorResponse(msg));
  }
}

export function* reorderPages(action: any): Generator<any, void, any> {
  try {
    const response = yield call(albumBuilderService.reorderPages, action.payload);
    if (response?.success) {
      yield put({ type: C.reorderPagesSuccess, payload: action.payload.pageOrder });
    } else {
      const msg = response?.message || "Failed to reorder pages";
      yield put({ type: C.reorderPagesFailure, payload: msg });
      yield put(commonActions.errorResponse(msg));
    }
  } catch (error: any) {
    const msg = error?.message || "An error occurred";
    yield put({ type: C.reorderPagesFailure, payload: msg });
    yield put(commonActions.errorResponse(msg));
  }
}

export function* saveAlbum(): Generator<any, void, any> {
  try {
    const dirtySheets: string[] = yield select((s: any) => s.albumBuilder.dirtySheets);
    const pages: Record<string, any> = yield select((s: any) => s.albumBuilder.pages);

    if (dirtySheets.length === 0) {
      yield put({ type: C.saveTemplateSuccess });
      return;
    }

    const payloadPages = dirtySheets
      .map((pageId) => {
        const page = pages[pageId];
        if (!page) return null;
        return {
          pageId,
          nodes: page.nodes,
          backgroundColor: page.backgroundColor,
          backgroundAssetId: page.backgroundAssetId,
          backgroundOpacity: page.backgroundOpacity,
          label: page.label,
        };
      })
      .filter(Boolean);

    if (!payloadPages.length) {
      yield put({ type: C.saveTemplateSuccess });
      return;
    }

    const response: any = yield call(albumBuilderService.savePages, payloadPages);

    if (response?.success) {
      for (const pageId of dirtySheets) {
        yield put({ type: C.saveSuccess, payload: pageId });
      }
      yield put({ type: C.saveTemplateSuccess });
    } else {
      const msg = response?.message || response?.errorMessage || "One or more sheets failed to save";
      yield put({ type: C.saveTemplateFailure, payload: msg });
      yield put(commonActions.errorResponse(msg));
    }
  } catch (error: any) {
    const msg = error?.message || "Save album failed";
    yield put({ type: C.saveTemplateFailure, payload: msg });
    yield put(commonActions.errorResponse(msg));
  }
}

export function* savePage(action: any): Generator<any, void, any> {
  try {
    const { pageId } = action.payload;
    const pages: Record<string, any> = yield select((s: any) => s.albumBuilder.pages);
    const page = pages[pageId];
    if (!page) {
      yield put({ type: C.saveFailure, payload: "Sheet not found" });
      return;
    }

    const response: any = yield call(albumBuilderService.savePages, [
      {
        pageId,
        nodes: page.nodes,
        backgroundColor: page.backgroundColor,
        backgroundAssetId: page.backgroundAssetId,
        backgroundOpacity: page.backgroundOpacity,
        label:  page.label,
      },
    ]);
    if (response?.success) {
      yield put({ type: C.saveSuccess, payload: pageId });
    } else {
      const msg = response?.message || response?.errorMessage || "Save failed";
      yield put({ type: C.saveFailure, payload: msg });
      yield put(commonActions.errorResponse(msg));
    }
  } catch (error: any) {
    const msg = error?.message || "Save failed";
    yield put({ type: C.saveFailure, payload: msg });
    yield put(commonActions.errorResponse(msg));
  }
}

export function* createBackgroundSas(action: PayloadAction<{ pageId: string; fileName: string }>): Generator<any, void, any> {
  try {
    const response = yield call(albumBuilderService.createBackgroundSas, action.payload);
    if (response?.success) {
      yield put({ type: C.createBackgroundSasSuccess, payload: response.resultObject });
    } else {
      const msg = response?.message || "Failed to generate background SAS URL";
      yield put({ type: C.createBackgroundSasFailure, payload: msg });
      yield put(commonActions.errorResponse(msg));
    }
  } catch (err: any) {
    const msg = err?.message || "An error occurred";
    yield put({ type: C.createBackgroundSasFailure, payload: msg });
    yield put(commonActions.errorResponse(msg));
  }
}